import { CompanyLocationSelectableEntityResponseDto } from "./job-offer";
import { Paginated } from "./utils";

export enum ValutationEnum {
  NONE = "Nicht bewertet",
  BAD = "Schlecht",
  NEUTRAL = "Mittel",
  GOOD = "Gut",
}

export enum ApplicantLogEvent {
  APPLICANT_CREATED = "Der Bewerber wurde erstellt",
  APPLICANT_DATA_UPDATED = "Der Bewerber wurde bearbeitet",
  APPLICANT_DELETED = "Der Bewerber wurde gelöscht",
  APPLICANT_ANONYMIZED = "Der Bewerber wurde Anonymisiert",
  APPLICANT_STATUS_CHANGED = "Status geändert",
  APPLICANT_REASSIGNED = "Bewerber wurde verschoben",
  APPLICANT_MAIL_SENT = "Dem Bewerber wurde eine E-Mail versendet",
}

export enum Genders {
  "NONE" = "Kein Geschlecht ausgewählt",
  "MALE" = "Herr",
  "FEMALE" = "Frau",
  "DIVERS" = "Divers",
}

export type WhatsAppAiStatus = string;

export interface AiWhatsAppAppointment {
  startsAt: string;
  timezone: string;
  status: string;
  requestedWording?: string;
  summary?: string;
  cancelReason?: string;
}

export interface ApplicantDocument {
  fileName?: string;
  mimeType?: string;
  caption?: string | null;
  source: "manual" | "whatsAppAi";
  receivedAt: string;
  url: string; // presigned, temporary download URL
}

export type WhatsAppChatRole = "assistant" | "user";
export type WhatsAppChatKind = "text" | "audio" | "template" | "button" | "image" | "video" | "document";

export interface WhatsAppChatMessage {
  role: WhatsAppChatRole; // assistant = Bot (eigene Seite/rechts), user = Bewerber (links)
  kind: WhatsAppChatKind;
  content?: string;
  createdAt: string; // ISO-8601 UTC, chronologisch
}

export interface WhatsAppChatResponseDto {
  messages: WhatsAppChatMessage[];
}

export interface CreateApplicantRequestDto {
  name: string;
  gender?: string;
  street?: string;
  postCode?: string;
  placeName?: string;
  phone?: string;
  mail?: string | null;
  notes?: string;
  belongsToCompanyLocationId?: string;
  belongsToJobOfferId?: string;
  valuation?: ValutationEnum;
  belongsToSource: string;
  belongsToStatus: string;
  applicationDate?: string;
  aiWhatsAppSummary?: string;
}

export interface ApplicantResponseDto {
  belongsToOrganizationId: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  name: string;
  gender: string;
  street: string;
  postCode: string;
  placeName: string;
  phone: string;
  mail: string;
  notes: string;
  belongsToCompanyLocationId: string;
  belongsToJobOfferId: string;
  companyLocationTitle: string;
  jobOfferTitle: string;
  lastEditedFrom: string;
  valuation: ValutationEnum;
  belongsToSource: string;
  belongsToStatus: string;
  applicationDate: string;
  aiWhatsAppSummary?: string;
}

export interface ApplicantListResponseDto {
  _id: string;
  name: string;
  gender: string;
  placeName: string;
  companyLocationTitle: string;
  jobOfferTitle: string;
  phone: string;
  valuation: ValutationEnum;
  belongsToStatus: string;
  applicationDate: string;
  belongsToSource: string;
  isAiWhatsAppApplicant: boolean;
  aiWhatsAppStatus?: WhatsAppAiStatus | null;
  aiWhatsAppAppointment?: AiWhatsAppAppointment | null;
  isHandedOver: boolean;
  optOut: boolean;
}

export interface JobOfferSelectableEntityResponseDto {
  _id: string;
  value: string;
  isArchive: boolean;
}

export interface ApplicantStatusSelectableEntityResponseDto {
  id: string;
  value: string;
  default: boolean;
  color: string;
  expires: boolean;
}

export interface ApplicantCompanyLocationSelectableEntityResponseDto {
  _id: string;
  value: string;
}

export interface ApplicantSourceSelectableEntityResponseDto {
  id: string;
  value: string;
}

export type ApplicantTab = "withAppointment" | "withoutAppointment" | "noResponse" | "direct";

export interface ApplicantTabCounts {
  withAppointment: number;
  withoutAppointment: number;
  noResponse: number;
  direct: number;
}

export interface ApplicantListPaginated extends Paginated<ApplicantListResponseDto> {
  tabCounts: ApplicantTabCounts;
}

export interface ApplicantListPaginatedResponseDtoWithSelectableEntities {
  jobOffers: JobOfferSelectableEntityResponseDto[];
  status: ApplicantStatusSelectableEntityResponseDto[];
  companyLocations: ApplicantCompanyLocationSelectableEntityResponseDto[];
  aiEnabled: boolean; // Feature-Flag (Orga hat KI grundsätzlich) — fürs generelle Gating
  showAiTabs: boolean; // steuert allein die Reiter-Anzeige (aiEnabled ODER es gibt noch KI-Bewerber)
  applicants: ApplicantListPaginated;
}

export interface ApplicantCreationEntitiesResponseDto {
  companyLocations: CompanyLocationSelectableEntityResponseDto[];
  foundCompanyLocationId: string;
  jobOffers: JobOfferSelectableEntityResponseDto[];
  status: ApplicantStatusSelectableEntityResponseDto[];
  sources: ApplicantSourceSelectableEntityResponseDto[];
  genders: Genders[];
}

export interface ApplicantCreationEntitiesByCompanyLocationIdResponseDto {
  jobOffers: JobOfferSelectableEntityResponseDto[];
}

export interface ApplicantListEntitiesResponseDto {
  status: ApplicantStatusSelectableEntityResponseDto[];
  sources: ApplicantSourceSelectableEntityResponseDto[];
}

export interface ApplicantSelectableEntitiesResponseDto {
  companyLocations: CompanyLocationSelectableEntityResponseDto[];
  foundCompanyLocationId: string;
  jobOffers: JobOfferSelectableEntityResponseDto[];
  status: ApplicantStatusSelectableEntityResponseDto[];
  sources: CompanyLocationSelectableEntityResponseDto[];
}

export interface ApplicantDetailsResponseDto {
  _id: string;
  name: string;
  gender: string;
  street: string;
  postCode: string;
  placeName: string;
  phone: string;
  mail: string;
  notes: string;
  belongsToCompanyLocationId: string;
  belongsToJobOfferId: string;
  lastEditedFrom: string;
  valuation: ValutationEnum;
  belongsToSource: string;
  sourceTitle: string;
  belongsToStatus: string;
  applicationDate: string;
  applicantLogs: ApplicantLog[];
  selectableEntities: ApplicantCreationEntitiesResponseDto;
  isEditable: boolean;
  companyLocationTitle: string;
  jobOfferTitle: string;
  aiWhatsAppSummary?: string;
  aiWhatsAppStatus?: WhatsAppAiStatus | null;
  whatsAppId?: string;
  appointmentDate?: string;
  isAiWhatsAppApplicant: boolean;
  isHandedOver: boolean;
  optOut: boolean;
  aiWhatsAppAppointment?: AiWhatsAppAppointment | null;
  documents: ApplicantDocument[];
}

export interface ApplicantLog {
  _id: string;
  createdAt: string;
  event: ApplicantLogEvent;
  information?: string;
}

export interface UpdateApplicantRequestDto {
  name: string;
  gender: string;
  street: string;
  postCode: string;
  placeName: string;
  phone: string;
  mail: string;
  notes: string;
  belongsToCompanyLocationId: string;
  belongsToJobOfferId: string;
  valuation: ValutationEnum;
  belongsToSource: string;
  belongsToStatus: string;
  applicationDate: string;
}

export interface ApplicantMailTemplateWithValuesResponseDto {
  _id: string;
  title: string;
  template: string;
  subject: string;
  receiver: string;
  placeholders: ApplicantMailTemplatePlaceholderValueResponseDto[];
  from?: {
    name: string;
    addressPrefix: string;
  };
  replyTo?: {
    name: string;
    address: string;
  };
}

export interface ApplicantMailTemplatePlaceholderValueResponseDto {
  placeholder: string;
  value: string;
}

export interface ApplicantMailTemplatesResponseDto {
  labels: MailTemplateLabelSelectableListWithTemplatesResponseDto[];
}

export interface MailTemplateLabelSelectableListWithTemplatesResponseDto {
  _id: string;
  value: string;
  mailTemplates: ApplicantMailTemplateListResponseDto[];
}

export interface ApplicantMailTemplateListResponseDto {
  _id: string;
  value: string;
}

export interface ApplicantLogMailResponseDto {
  _id: string;
  createdAt: string;
  event: ApplicantLogEvent;
  userName: string;
  information?: string;
  text: string;
  subject: string;
  fromName: string;
  fromAddressPrefix: string;
  replyToName: string;
  replyToAddress: string;
  to: string;
}
